import React, { useState } from "react";
import { X, Sparkles, DollarSign } from "lucide-react";
import { api } from "../lib/api";

/**
 * CustomOfferModal — Concierges send a structured Custom Offer to a customer
 * for an existing booking request. Customer can Accept / Decline from their
 * dashboard. The offer becomes the new booking amount + terms on accept.
 *
 * Props:
 *   booking: the booking object the offer is responding to
 *   open: bool — modal open state
 *   onClose: () => void
 *   onSent: () => void  // called after a successful POST so the dashboard reloads
 */
export default function CustomOfferModal({ booking, open, onClose, onSent }) {
  const [form, setForm] = useState({
    title: booking?.experience?.title || "Custom experience",
    overview: "",
    timeline: "",
    services: "", // comma-separated
    price: booking?.amount || 0,
    deposit_percent: 30,
    cancellation_policy: "flexible",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!open || !booking) return null;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/offers", {
        booking_id: booking.booking_id,
        title: form.title,
        overview: form.overview,
        included_services: form.services.split(",").map((s) => s.trim()).filter(Boolean),
        timeline: form.timeline,
        price: Number(form.price) || 0,
        deposit_percent: Number(form.deposit_percent) || 0,
        cancellation_policy: form.cancellation_policy,
        notes: form.notes,
      });
      onSent?.();
      onClose?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Failed to send offer");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-[#071A2D]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        data-testid="custom-offer-modal"
        className="card-luxury-elevated rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between p-6 pb-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold flex items-center gap-1.5">
              <Sparkles size={12} /> Custom Offer Builder
            </div>
            <h3 className="font-serif text-2xl mt-2">Send a Custom Offer</h3>
            <p className="text-xs text-[#071A2D]/65 mt-1">
              For {booking?.customer?.name || "the customer"} · Request: {booking?.experience?.title}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="custom-offer-close"
            className="p-1 rounded-full hover:bg-[#071A2D]/5"
            aria-label="Close offer modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 pt-3 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Experience title</label>
            <input
              data-testid="offer-field-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="kbd-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Overview</label>
            <textarea
              data-testid="offer-field-overview"
              rows="3"
              placeholder="A 1-2 sentence pitch of what you're proposing…"
              value={form.overview}
              onChange={(e) => setForm({ ...form, overview: e.target.value })}
              className="kbd-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Included services (comma-separated)</label>
            <input
              data-testid="offer-field-services"
              placeholder="e.g. Private chef · Sommelier wine · Florals · Photographer"
              value={form.services}
              onChange={(e) => setForm({ ...form, services: e.target.value })}
              className="kbd-input"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Timeline / itinerary</label>
            <textarea
              data-testid="offer-field-timeline"
              rows="3"
              placeholder="e.g. 7:00 PM Private car pickup · 7:45 PM Rooftop dinner · 10:30 PM Helicopter transfer…"
              value={form.timeline}
              onChange={(e) => setForm({ ...form, timeline: e.target.value })}
              className="kbd-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70 flex items-center gap-1">
                <DollarSign size={11} /> Total price
              </label>
              <input
                data-testid="offer-field-price"
                type="number"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="kbd-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Deposit %</label>
              <input
                data-testid="offer-field-deposit"
                type="number"
                min="0"
                max="100"
                value={form.deposit_percent}
                onChange={(e) => setForm({ ...form, deposit_percent: e.target.value })}
                className="kbd-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Cancellation policy</label>
            <select
              data-testid="offer-field-cancel"
              value={form.cancellation_policy}
              onChange={(e) => setForm({ ...form, cancellation_policy: e.target.value })}
              className="kbd-input"
            >
              <option value="flexible">Flexible — full refund 48h before</option>
              <option value="moderate">Moderate — 50% refund 48h before</option>
              <option value="strict">Strict — non-refundable</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-[#064E3B]/70">Notes (private)</label>
            <textarea
              data-testid="offer-field-notes"
              rows="2"
              placeholder="Anything else the customer should know about this proposal…"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="kbd-input"
            />
          </div>
        </div>

        {err && <div className="px-6 text-sm text-[#FF7A45]">{err}</div>}

        <div className="flex items-center justify-end gap-3 p-6 pt-2 border-t border-[#071A2D]/8 mt-4">
          <button
            onClick={onClose}
            data-testid="offer-cancel"
            className="btn-outline-emerald text-sm"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.title || !form.price}
            data-testid="offer-submit"
            className="btn-coral text-sm disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send offer to customer"}
          </button>
        </div>
      </div>
    </div>
  );
}
